import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  GraduationCap, Sparkles, FileCheck2, Tag, Users2, Home, Shield, ArrowLeft, Gift, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import GoogleLoginButton from "@/components/GoogleLoginButton";

const PERKS = [
  { icon: FileCheck2, label: "Démarches CAF · CROUS · Ameli simplifiées", color: "from-blue-500 to-blue-600" },
  { icon: Sparkles, label: "Assistant IA — CV, lettre, entretien", color: "from-violet-500 to-violet-600" },
  { icon: Home, label: "Logement & jobs étudiants", color: "from-cyan-500 to-cyan-600" },
  { icon: Tag, label: "Bons plans exclusifs SNCF · cinéma · sport", color: "from-amber-500 to-orange-500" },
  { icon: Users2, label: "Communauté locale par ville", color: "from-emerald-500 to-emerald-600" },
];

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = (params.get("ref") || "").toUpperCase();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [refCode, setRefCode] = useState(refFromUrl);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name, ref: refCode || undefined,
      });
      setUser(data);
      toast.success("Compte créé. Bienvenue !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Inscription échouée");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-white" data-testid="register-page">
      {/* Subtle bg accents (same as Login hero) */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[32rem] h-[32rem] rounded-full bg-violet-200/40 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[20rem] h-[20rem] rounded-full bg-cyan-100/50 blur-3xl" />
      </div>

      {/* Top nav */}
      <header className="relative z-10 px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="register-logo">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center shadow-lg shadow-violet-500/25 group-hover:scale-105 transition-transform">
            <GraduationCap size={22} />
          </div>
          <span className="font-display text-xl font-bold tracking-tight text-slate-900">Student Hub</span>
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100 transition-all"
          data-testid="nav-back-login"
        >
          <ArrowLeft size={14} /> Retour
        </Link>
      </header>

      <main className="relative z-10 px-6 sm:px-10 lg:px-16 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start max-w-7xl mx-auto pt-2 lg:pt-6">
          {/* Left — Welcome / perks */}
          <div className="lg:col-span-6 space-y-7" data-testid="register-welcome">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-xs font-medium text-violet-700">
              <Sparkles size={12} className="text-violet-600" /> Inscription gratuite · sans CB
            </div>

            <h1
              className="font-display text-5xl sm:text-6xl font-bold leading-[1.0] tracking-tight text-slate-900"
              data-testid="register-title"
            >
              Rejoins la{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                  promo 2026
                </span>
                <span className="absolute -bottom-1 left-0 right-0 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 opacity-80"></span>
              </span>
            </h1>

            <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
              Crée ton compte en 30 secondes et accède à <strong className="text-slate-900">tous les outils</strong> dont tu as
              besoin pour réussir ta vie étudiante.
            </p>

            {/* Perks */}
            <ul className="space-y-2.5 pt-1" data-testid="register-perks">
              {PERKS.map((p) => {
                const Icon = p.icon;
                return (
                  <li key={p.label} className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center shadow shrink-0`}>
                      <Icon size={16} className="text-white" />
                    </div>
                    <p className="text-sm text-slate-700">{p.label}</p>
                  </li>
                );
              })}
            </ul>

            {/* Trial banner */}
            <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-blue-100 border border-emerald-200 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center shrink-0">
                <Crown size={18} className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">7 jours Premium offerts</p>
                <p className="text-xs text-slate-600">Sans carte bancaire requise. Annulable en 1 clic.</p>
              </div>
            </div>

            {/* Trust */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-1 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><Shield size={13} className="text-emerald-500" /> RGPD · données chiffrées</span>
              <span className="flex items-center gap-1.5"><Crown size={13} className="text-yellow-500" /> Sans engagement · annulation 1 clic</span>
            </div>
          </div>

          {/* Right — Form card */}
          <div className="lg:col-span-6">
            <div
              className="rounded-3xl bg-white shadow-2xl shadow-violet-300/30 ring-1 ring-slate-200/60 p-7 sm:p-9"
              data-testid="register-card"
            >
              <h2 className="font-display text-2xl font-bold text-slate-900 mb-1">Crée ton compte</h2>
              <p className="text-sm text-slate-500 mb-6">C'est gratuit, sans CB, et tu peux supprimer ton compte à tout moment.</p>

              {refFromUrl && (
                <div className="mb-5 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-start gap-2 text-sm" data-testid="referral-banner">
                  <Gift size={16} className="text-violet-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">
                    Tu as été <strong>parrainé·e</strong> ! Ton parrain débloquera des avantages quand tu valides ton inscription.
                  </span>
                </div>
              )}

              <GoogleLoginButton label="S'inscrire avec Google" testid="register-google-btn" />
              <div className="my-5 flex items-center gap-3" data-testid="register-divider">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-xs text-slate-400 uppercase tracking-wider">ou avec ton email</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="name" className="text-slate-700 text-xs">Prénom / Pseudo</Label>
                  <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700 text-xs">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="tonemail@etudiant.fr" data-testid="register-email-input" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-slate-700 text-xs">Mot de passe (min. 6 caractères)</Label>
                  <PasswordInput id="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="••••••••" data-testid="register-password-input" />
                </div>
                <div>
                  <Label htmlFor="ref" className="text-slate-700 text-xs">Code de parrainage <span className="text-slate-400">(optionnel)</span></Label>
                  <Input
                    id="ref"
                    value={refCode}
                    onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                    placeholder="ABC123"
                    className="mt-1.5 font-mono tracking-widest"
                    data-testid="register-ref-input"
                  />
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed pt-1">
                  En créant ton compte, tu acceptes nos{" "}
                  <Link to="/legal/cgu" className="text-violet-600 hover:underline">CGU</Link>{" "}et notre{" "}
                  <Link to="/legal/confidentialite" className="text-violet-600 hover:underline">politique de confidentialité</Link>.
                </p>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full text-base font-semibold bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 shadow-lg shadow-violet-600/30 hover:shadow-violet-600/50 hover:scale-[1.01] active:scale-100 transition-all border-0"
                  data-testid="register-submit-btn"
                >
                  {loading ? "Création..." : "Créer mon compte gratuit"}
                </Button>
              </form>

              <p className="text-sm text-slate-500 mt-6 text-center">
                Déjà inscrit ?{" "}
                <Link to="/login" className="text-violet-600 font-medium hover:underline" data-testid="go-login-link">
                  Se connecter
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
